import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Beer, Scroll } from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ email: '', password: '', name: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success('Willkommen zurück!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Anmeldung fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await register(registerForm.email, registerForm.password, registerForm.name);
      toast.success('Registrierung erfolgreich!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registrierung fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1758952529044-9727671a8e4a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxoaXN0b3JpYyUyMHN0b25lJTIwd2luZSUyMGNlbGxhciUyMHJlc3RhdXJhbnQlMjB2YXVsdGVkJTIwY2VpbGluZ3xlbnwwfHx8fDE3NzE2ODA5NDZ8MA&ixlib=rb-4.1.0&q=85)'
        }}
      />
      <div className="absolute inset-0 bg-black/60" />
      
      {/* Login Card */}
      <Card className="relative w-full max-w-md glass border-gold/20 animate-fade-in" data-testid="login-card">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center items-center gap-3">
            <Beer className="h-10 w-10 text-gold" />
            <div>
              <h1 className="font-serif text-3xl font-bold text-burgundy">Mautkeller</h1>
              <p className="text-xs tracking-widest text-charcoal-600 uppercase">Brauhaus Nürnberg</p>
            </div>
          </div>
          <div className="flex items-center gap-2 justify-center text-gold">
            <Scroll className="h-5 w-5" />
            <CardTitle className="font-serif text-xl text-charcoal-800">VA-Facts Manager</CardTitle>
          </div>
          <CardDescription className="text-charcoal-600">
            Veranstaltungen planen & Function Sheets erstellen
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="login-tab">Anmelden</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Registrieren</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">E-Mail</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="mail@beispiel.de"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    required
                    data-testid="login-email-input"
                    className="border-stone-300 focus:border-burgundy focus:ring-burgundy"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Passwort</Label>
                  <Input
                    id="login-password"
                    type="password"
                    placeholder="••••••••"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    required
                    data-testid="login-password-input"
                    className="border-stone-300 focus:border-burgundy focus:ring-burgundy"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-burgundy hover:bg-burgundy-800 text-white font-serif tracking-wide transition-all duration-200"
                  disabled={isLoading}
                  data-testid="login-submit-btn"
                >
                  {isLoading ? 'Wird angemeldet...' : 'Anmelden'}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="register-name">Name</Label>
                  <Input
                    id="register-name"
                    type="text"
                    placeholder="Max Mustermann"
                    value={registerForm.name}
                    onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                    required
                    data-testid="register-name-input"
                    className="border-stone-300 focus:border-burgundy focus:ring-burgundy"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-email">E-Mail</Label>
                  <Input
                    id="register-email"
                    type="email"
                    placeholder="mail@beispiel.de"
                    value={registerForm.email}
                    onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                    required
                    data-testid="register-email-input"
                    className="border-stone-300 focus:border-burgundy focus:ring-burgundy"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-password">Passwort</Label>
                  <Input
                    id="register-password"
                    type="password"
                    placeholder="••••••••"
                    value={registerForm.password}
                    onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                    required
                    data-testid="register-password-input"
                    className="border-stone-300 focus:border-burgundy focus:ring-burgundy"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-burgundy hover:bg-burgundy-800 text-white font-serif tracking-wide transition-all duration-200"
                  disabled={isLoading}
                  data-testid="register-submit-btn"
                >
                  {isLoading ? 'Wird registriert...' : 'Registrieren'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
